import React, { KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import Input from '@components/Input';
import OnClickOutside from '@components/OnClickOutside';
import { useDebounce } from '@hooks/index';
import { Avatar as ClientsIcon, Luggage as OrdersIcon } from '@icons/common';

import {
  AgentApiError,
  getAgentClients,
  getAgentOrders,
  serializeAgentClient,
  serializeAgentOrder,
} from '../api';
import { AGENT_AUTH_ROUTE } from '../auth/config';
import { withAppBasePath } from '../../appMode';
import { AGENT_CLIENTS_ROUTE } from '../clients';
import { B2BClient } from '../clients/types';
import { AGENT_ORDERS_ROUTE } from '../orders';
import { B2BOrder } from '../orders/types';

const RESULTS_PER_SECTION = 5;

const AgentGlobalSearch: React.FC = () => {
  const history = useHistory();
  const requestId = useRef(0);
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [orders, setOrders] = useState<B2BOrder[]>([]);
  const [clients, setClients] = useState<B2BClient[]>([]);
  const [isLoadingRemote, setIsLoadingRemote] = useState(false);
  const debouncedQuery = useDebounce(query.trim(), 300);

  useEffect(() => {
    if (!debouncedQuery) {
      setOrders([]);
      setClients([]);
      setIsLoadingRemote(false);
      return undefined;
    }

    const currentRequestId = requestId.current + 1;
    requestId.current = currentRequestId;
    setIsLoadingRemote(true);

    Promise.all([
      getAgentOrders({
        query: debouncedQuery,
        page: 1,
        perPage: RESULTS_PER_SECTION,
      }),
      getAgentClients({
        query: debouncedQuery,
        page: 1,
        perPage: RESULTS_PER_SECTION,
      }),
    ])
      .then(([ordersResponse, clientsResponse]) => {
        if (requestId.current !== currentRequestId) return;

        setOrders(ordersResponse.orders.map(serializeAgentOrder));
        setClients(clientsResponse.clients.map((item) => serializeAgentClient(item)));
      })
      .catch((error) => {
        if (requestId.current !== currentRequestId) return;

        setOrders([]);
        setClients([]);

        if (error instanceof AgentApiError && error.status === 401) {
          window.location.assign(withAppBasePath(AGENT_AUTH_ROUTE));
        }
      })
      .finally(() => {
        if (requestId.current === currentRequestId) {
          setIsLoadingRemote(false);
        }
      });

    return () => {
      requestId.current += 1;
    };
  }, [debouncedQuery]);

  const normalizedQuery = query.trim();
  const results = useMemo(() => {
    if (!normalizedQuery) {
      return { clients: [], orders: [] };
    }

    return {
      orders,
      clients,
    };
  }, [clients, normalizedQuery, orders]);

  const resultsCount = results.orders.length + results.clients.length;

  const openResult = (path: string) => {
    setQuery('');
    setIsOpen(false);
    history.push(path);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') setIsOpen(false);
  };

  return (
    <Root onClickOutside={() => setIsOpen(false)}>
      <SearchInput
        aria-label="Глобальный поиск"
        placeholder="Заказы или клиенты…"
        value={query}
        onChange={(event) => {
          const nextQuery = event.target.value;

          requestId.current += 1;
          setQuery(nextQuery);
          setOrders([]);
          setClients([]);
          setIsLoadingRemote(Boolean(nextQuery.trim()));
          setIsOpen(Boolean(nextQuery.trim()));
        }}
        onFocus={() => setIsOpen(Boolean(normalizedQuery))}
        onKeyDown={handleKeyDown}
      />

      {isOpen && (
        <Dropdown>
          {results.orders.length > 0 && (
            <Section>
              <SectionTitle>Заказы</SectionTitle>
              {results.orders.map((order) => (
                <ResultButton
                  key={order.id}
                  type="button"
                  onClick={() =>
                    openResult(`${AGENT_ORDERS_ROUTE}/${order.id}`)
                  }
                >
                  <ResultIconCircle>
                    <OrderResultIcon />
                  </ResultIconCircle>
                  <ResultText>
                    <strong>
                      Заказ №{order.id} · {order.touristName}
                    </strong>
                    <span>
                      {order.dates} · {order.hotelName}
                    </span>
                  </ResultText>
                </ResultButton>
              ))}
            </Section>
          )}

          {results.clients.length > 0 && (
            <Section>
              <SectionTitle>Клиенты</SectionTitle>
              {results.clients.map((client) => (
                <ResultButton
                  key={client.id}
                  type="button"
                  onClick={() =>
                    openResult(`${AGENT_CLIENTS_ROUTE}/${client.id}`)
                  }
                >
                  <ResultIconCircle>
                    <ClientResultIcon />
                  </ResultIconCircle>
                  <ResultText>
                    <strong>{client.fullName}</strong>
                    <span>
                      {client.phone} · {client.email}
                    </span>
                  </ResultText>
                </ResultButton>
              ))}
            </Section>
          )}

          {!resultsCount && (
            <EmptyResult>
              {isLoadingRemote ? 'Ищем…' : 'Ничего не найдено'}
            </EmptyResult>
          )}
        </Dropdown>
      )}
    </Root>
  );
};

const Root = styled(OnClickOutside)`
  position: relative;
  width: 100%;
`;

const SearchInput = styled(Input)`
  border: solid 1px #c2c5cc;
  border-radius: 4px;
  font-size: 13px;
  height: 40px;
  width: 100%;
`;

const Dropdown = styled.div`
  background: #fff;
  border: 1px solid #dfe2e6;
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(24, 35, 48, 0.14);
  left: 0;
  max-height: min(560px, calc(100vh - 90px));
  overflow-y: auto;
  position: absolute;
  right: 0;
  top: 44px;
  z-index: 12;
`;

const Section = styled.section`
  padding: 8px 0;

  & + & {
    border-top: 1px solid #eceef0;
  }
`;

const SectionTitle = styled.div`
  color: #919499;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  padding: 4px 12px 6px;
  text-transform: uppercase;
`;

const ResultButton = styled.button`
  align-items: center;
  background: #fff;
  border: 0;
  cursor: pointer;
  display: flex;
  gap: 10px;
  min-height: 52px;
  padding: 7px 12px;
  text-align: left;
  width: 100%;

  &:hover,
  &:focus {
    background: #f5f7f8;
    outline: none;
  }
`;

const ResultIconCircle = styled.div`
  align-items: center;
  background: #eef4ff;
  border-radius: 50%;
  color: #326fd1;
  display: flex;
  flex: 0 0 32px;
  height: 32px;
  justify-content: center;
  width: 32px;
`;

const OrderResultIcon = styled(OrdersIcon)`
  height: 18px;
  width: 18px;

  &,
  * {
    fill: none;
    stroke: currentColor;
    stroke-width: 1px;
    vector-effect: non-scaling-stroke;
  }
`;

const ClientResultIcon = styled(ClientsIcon)`
  height: 22px;
  width: 22px;

  circle {
    display: none;
  }

  path {
    stroke: currentColor;
    stroke-width: 1px;
    vector-effect: non-scaling-stroke;
  }
`;

const ResultText = styled.div`
  display: grid;
  gap: 3px;
  min-width: 0;

  strong,
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    color: #17181a;
    font-size: 13px;
    font-weight: 600;
  }

  span {
    color: #73767c;
    font-size: 12px;
  }
`;

const EmptyResult = styled.div`
  color: #73767c;
  font-size: 13px;
  padding: 20px;
  text-align: center;
`;

export default AgentGlobalSearch;
